import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContactService from "../../apis/ContactService";

const createInitialValues = () => ({
  name: "",
  email: "",
  phone: "",
  message: "",
  captchaInput: "",
});

const requiredFields = {
  name: "Name",
  email: "Email",
  message: "Message",
};

const generateCaptcha = () => {
  const first = Math.floor(Math.random() * 9) + 1;
  const second = Math.floor(Math.random() * 9) + 1;

  return {
    question: `What is ${first} + ${second}?`,
    answer: String(first + second),
  };
};

const ContactForm = ({
  submitButtonLabel = "Send Message",
  successMessage = "Thank you for reaching out! We'll be in touch soon.",
  sx,
}) => {
  const [values, setValues] = useState(() => createInitialValues());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [captcha, setCaptcha] = useState(() => generateCaptcha());

  const requiredFieldEntries = useMemo(() => Object.entries(requiredFields), []);

  const validate = () => {
    const nextErrors = {};

    requiredFieldEntries.forEach(([key, label]) => {
      const value = values[key]?.trim();

      if (!value) {
        nextErrors[key] = `${label} is required.`;
      }
    });

    if (values.email?.trim()) {
      const emailPattern = /.+@.+\..+/i;
      if (!emailPattern.test(values.email.trim())) {
        nextErrors.email = "Enter a valid email address.";
      }
    }

    const captchaInput = values.captchaInput?.trim();
    if (!captchaInput) {
      nextErrors.captchaInput = "Please solve the captcha.";
    } else if (captchaInput !== captcha.answer) {
      nextErrors.captchaInput = "Captcha answer is incorrect.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitSuccess(false);
    setSubmitError("");

    if (!validate()) {
      return;
    }

    try {
      setSubmitting(true);
      await ContactService.submitContactRequest({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        message: values.message.trim(),
      });

      setSubmitSuccess(true);
      setValues(createInitialValues());
      setErrors({});
      setCaptcha(generateCaptcha());
    } catch (error) {
      console.error("Failed to submit contact request", error);
      setSubmitError(
        error?.response?.data?.message ||
          error?.message ||
          "We were unable to submit your message. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setValues((prev) => ({
      ...prev,
      captchaInput: "",
    }));
    setErrors((prev) => {
      const { captchaInput: _removed, ...rest } = prev;
      return rest;
    });
  };

  return (
    <Stack spacing={3} sx={{ width: "100%", maxWidth: 600, ...sx }}>
      {submitSuccess && (
        <Alert severity="success" onClose={() => setSubmitSuccess(false)}>
          {successMessage}
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" onClose={() => setSubmitError("")}>
          {submitError}
        </Alert>
      )}

      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={values.name}
              onChange={handleChange}
              error={Boolean(errors.name)}
              helperText={errors.name || ""}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email || ""}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={values.phone}
              onChange={handleChange}
              error={Boolean(errors.phone)}
              helperText={errors.phone || ""}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="How can we help?"
              name="message"
              multiline
              minRows={4}
              value={values.message}
              onChange={handleChange}
              error={Boolean(errors.message)}
              helperText={errors.message || ""}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Captcha</Typography>
              <Typography color="text.secondary">{captcha.question}</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  required
                  fullWidth
                  label="Your Answer"
                  name="captchaInput"
                  value={values.captchaInput}
                  onChange={handleChange}
                  error={Boolean(errors.captchaInput)}
                  helperText={errors.captchaInput || ""}
                />
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleRefreshCaptcha}
                  disabled={submitting}
                >
                  New Question
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                {submitting ? "Sending…" : submitButtonLabel}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};

export default ContactForm;
